import { useEffect, useMemo, useState } from "react";
import { AppShell } from "@/components/layout/AppShell";
import { PageHeader } from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { useBusiness } from "@/hooks/use-business";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Search, LogIn, LogOut, Plus, Printer, Download } from "lucide-react";
import { Link } from "react-router-dom";
import { format, startOfDay, addDays, startOfWeek, startOfMonth, addMonths } from "date-fns";
import { buildPaystackCallbackUrl } from "@/lib/payments";
import { getBaseUrl } from "@/lib/site";

type SearchResult =
  | { kind: "member"; membership: any; customer: any }
  | { kind: "walkin"; dayPass: any; customer: any };

export default function AccessDesk() {
  const { toast } = useToast();
  const { business } = useBusiness();
  // NOTE: our local generated `types.ts` is not updated for the new module tables yet,
  // so we use an untyped supabase client in this page.
  const sb = supabase as any;

  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<SearchResult[]>([]);

  const [openSessions, setOpenSessions] = useState<any[]>([]);
  const [refreshKey, setRefreshKey] = useState(0);

  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summary, setSummary] = useState({
    visitsToday: 0,
    memberVisitsToday: 0,
    walkInVisitsToday: 0,
    walkInRevenueToday: 0,
  });

  const [walkInOpen, setWalkInOpen] = useState(false);
  const [isCreatingWalkIn, setIsCreatingWalkIn] = useState(false);
  const [walkIn, setWalkIn] = useState({
    name: "",
    phone: "",
    email: "",
    amount: "",
    method: "cash",
    partySize: "1",
  });
  const [startingPaystackPayment, setStartingPaystackPayment] = useState(false);

  const today = useMemo(() => new Date(), []);
  const todayLabel = format(today, "EEE, MMM d");
  const todayDateStr = format(new Date(), "yyyy-MM-dd");

  type RangePreset = "today" | "week" | "month" | "custom";
  const [rangePreset, setRangePreset] = useState<RangePreset>("today");
  const [customFrom, setCustomFrom] = useState<string>(format(new Date(), "yyyy-MM-dd"));
  const [customTo, setCustomTo] = useState<string>(format(new Date(), "yyyy-MM-dd"));
  const [visitsLoading, setVisitsLoading] = useState(false);
  const [visitsRows, setVisitsRows] = useState<any[]>([]);

  const fetchOpenSessions = async () => {
    if (!business?.id) return;
    const { data, error } = await sb
      .from("access_sessions")
      .select("id,customer:customers(id,name,phone),check_in_at,membership_id,day_pass_id,party_size")
      .eq("business_id", business.id)
      .is("check_out_at", null)
      .order("check_in_at", { ascending: false })
      .limit(20);
    if (!error) setOpenSessions((data || []) as any);
  };

  const fetchTodaySummary = async () => {
    if (!business?.id) return;
    setSummaryLoading(true);
    try {
      const start = startOfDay(new Date());
      const end = addDays(start, 1);

      const [{ data: sessions, error: sErr }, { data: dayPasses, error: pErr }] = await Promise.all([
        sb
          .from("access_sessions")
          .select("id,membership_id,day_pass_id,check_in_at,party_size")
          .eq("business_id", business.id)
          .gte("check_in_at", start.toISOString())
          .lt("check_in_at", end.toISOString()),
        sb
          .from("day_passes")
          .select("amount")
          .eq("business_id", business.id)
          .eq("pass_date", todayDateStr as any),
      ]);

      if (sErr) throw sErr;
      if (pErr) throw pErr;

      const all = sessions || [];
      const memberVisits = all.filter((x: any) => !!x.membership_id).length;
      const memberVisitsWeighted = all
        .filter((x: any) => !!x.membership_id)
        .reduce((sum: number, x: any) => sum + Number(x.party_size || 1), 0);
      const walkInVisitsWeighted = all
        .filter((x: any) => !!x.day_pass_id)
        .reduce((sum: number, x: any) => sum + Number(x.party_size || 1), 0);
      const totalVisitsWeighted = all.reduce((sum: number, x: any) => sum + Number(x.party_size || 1), 0);
      const revenue = (dayPasses || []).reduce((sum: number, r: any) => sum + Number(r.amount || 0), 0);

      setSummary({
        visitsToday: totalVisitsWeighted,
        memberVisitsToday: memberVisitsWeighted,
        walkInVisitsToday: walkInVisitsWeighted,
        walkInRevenueToday: revenue,
      });
    } catch (e: any) {
      // keep page usable even if summary fails
      console.warn("Failed to load Access Desk summary:", e?.message || e);
    } finally {
      setSummaryLoading(false);
    }
  };

  const getRange = () => {
    const now = new Date();
    if (rangePreset === "today") {
      const start = startOfDay(now);
      return { start, end: addDays(start, 1), label: "Today" };
    }
    if (rangePreset === "week") {
      const start = startOfWeek(now, { weekStartsOn: 1 });
      return { start, end: addDays(start, 7), label: "This week" };
    }
    if (rangePreset === "month") {
      const start = startOfMonth(now);
      return { start, end: addMonths(start, 1), label: "This month" };
    }
    // custom (inclusive end date)
    const start = startOfDay(new Date(customFrom));
    const end = addDays(startOfDay(new Date(customTo)), 1);
    return { start, end, label: "Custom" };
  };

  const fetchVisitsLog = async () => {
    if (!business?.id) return;
    setVisitsLoading(true);
    try {
      const { start, end } = getRange();
      const { data, error } = await sb
        .from("access_sessions")
        .select(
          `
          id,
          check_in_at,
          check_out_at,
          party_size,
          customer:customers(id,name,phone),
          membership:memberships(id,membership_code, plan:membership_plans(name)),
          day_pass:day_passes(id,ticket_code,amount,currency,party_size)
        `,
        )
        .eq("business_id", business.id)
        .gte("check_in_at", start.toISOString())
        .lt("check_in_at", end.toISOString())
        .order("check_in_at", { ascending: false })
        .limit(500);
      if (error) throw error;
      setVisitsRows(data || []);
    } catch (e: any) {
      toast({ title: "Failed to load visits", description: e?.message || "Try again.", variant: "destructive" });
    } finally {
      setVisitsLoading(false);
    }
  };

  useEffect(() => {
    fetchOpenSessions();
    fetchTodaySummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [business?.id, refreshKey]);

  useEffect(() => {
    fetchVisitsLog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [business?.id, refreshKey, rangePreset, customFrom, customTo]);

  const exportVisitsCSV = () => {
    const rows = visitsRows.map((r: any) => {
      const isMember = !!r.membership_id || !!r.membership;
      const name = r.customer?.name || "—";
      const type = isMember ? "Member" : "Walk-in";
      const membershipType = isMember ? (r.membership?.plan?.name || "Membership") : "Day Pass";
      const code = isMember ? r.membership?.membership_code : r.day_pass?.ticket_code;
      const partySize = Number(r.party_size || r.day_pass?.party_size || 1);
      const checkIn = r.check_in_at ? format(new Date(r.check_in_at), "yyyy-MM-dd h:mm a") : "";
      const checkOut = r.check_out_at ? format(new Date(r.check_out_at), "yyyy-MM-dd h:mm a") : "";
      const amount = !isMember ? Number(r.day_pass?.amount || 0) : 0;
      const currency = !isMember ? String(r.day_pass?.currency || (business as any)?.currency || "GHS") : "";
      return {
        name,
        type,
        membership_type: membershipType,
        code,
        party_size: partySize,
        check_in: checkIn,
        check_out: checkOut,
        walkin_amount: amount ? `${currency} ${amount.toFixed(2)}` : "",
      };
    });
    if (!rows.length) return;
    const headers = Object.keys(rows[0]);
    const esc = (v: any) => {
      const s = v == null ? "" : String(v);
      return `"${s.replace(/"/g, '""')}"`;
    };
    const csv = [headers.join(","), ...rows.map((r: any) => headers.map((h) => esc(r[h])).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const range = getRange();
    a.href = url;
    a.download = `access_desk_visits_${format(range.start, "yyyyMMdd")}_${format(addDays(range.end, -1), "yyyyMMdd")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const search = async () => {
    if (!business?.id) return;
    const q = query.trim();
    if (!q) return;
    setLoading(true);
    try {
      // Step 1: find matching customers (name/phone/email)
      const { data: customers, error: cErr } = await sb
        .from("customers")
        .select("id")
        .eq("business_id", business.id)
        .or(`name.ilike.%${q}%,phone.ilike.%${q}%,email.ilike.%${q}%`)
        .limit(50);
      if (cErr) throw cErr;
      const customerIds = (customers || []).map((c: any) => c.id);

      // Step 2: find memberships + today's day passes by code OR customer_id match
      const membershipQuery = sb
        .from("memberships")
        .select("*, customer:customers(id,name,phone,email)")
        .eq("business_id", business.id)
        .ilike("membership_code", `%${q}%`)
        .order("created_at", { ascending: false })
        .limit(20);

      const membershipByCustomerQuery =
        customerIds.length > 0
          ? sb
              .from("memberships")
              .select("*, customer:customers(id,name,phone,email)")
              .eq("business_id", business.id)
              .in("customer_id", customerIds)
              .order("created_at", { ascending: false })
              .limit(20)
          : null;

      const passQuery = sb
        .from("day_passes")
        .select("*, customer:customers(id,name,phone,email)")
        .eq("business_id", business.id)
        .eq("pass_date", todayDateStr as any)
        .ilike("ticket_code", `%${q}%`)
        .order("created_at", { ascending: false })
        .limit(20);

      const passByCustomerQuery =
        customerIds.length > 0
          ? sb
              .from("day_passes")
              .select("*, customer:customers(id,name,phone,email)")
              .eq("business_id", business.id)
              .eq("pass_date", todayDateStr as any)
              .in("customer_id", customerIds)
              .order("created_at", { ascending: false })
              .limit(20)
          : null;

      const [memByCode, memByCustomer, passByCode, passByCustomer] = await Promise.all([
        membershipQuery,
        membershipByCustomerQuery ?? Promise.resolve({ data: [] as any[], error: null as any }),
        passQuery,
        passByCustomerQuery ?? Promise.resolve({ data: [] as any[], error: null as any }),
      ]);

      if (memByCode.error) throw memByCode.error;
      if (memByCustomer.error) throw memByCustomer.error;
      if (passByCode.error) throw passByCode.error;
      if (passByCustomer.error) throw passByCustomer.error;

      const mems = [...(memByCode.data || []), ...(memByCustomer.data || [])];
      const passes = [...(passByCode.data || []), ...(passByCustomer.data || [])];

      // De-dupe by id
      const memMap = new Map<string, any>();
      for (const m of mems) memMap.set(m.id, m);
      const passMap = new Map<string, any>();
      for (const p of passes) passMap.set(p.id, p);

      const mapped: SearchResult[] = [
        ...Array.from(memMap.values()).map((m: any) => ({ kind: "member" as const, membership: m, customer: m.customer })),
        ...Array.from(passMap.values()).map((p: any) => ({ kind: "walkin" as const, dayPass: p, customer: p.customer })),
      ];

      setResults(mapped);
    } catch (e: any) {
      toast({ title: "Search failed", description: e?.message || "Try again.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const checkIn = async (r: SearchResult) => {
    if (!business?.id) return;
    const customerId = r.customer?.id;
    if (!customerId) return;

    try {
      // prevent duplicate open session for same person
      const { data: existing } = await sb
        .from("access_sessions")
        .select("id")
        .eq("business_id", business.id)
        .eq("customer_id", customerId)
        .is("check_out_at", null)
        .maybeSingle();
      if (existing?.id) {
        toast({ title: "Already checked in", description: "This person is currently checked in." });
        return;
      }

      const payload: any = {
        business_id: business.id,
        customer_id: customerId,
        membership_id: r.kind === "member" ? r.membership.id : null,
        day_pass_id: r.kind === "walkin" ? r.dayPass.id : null,
        check_in_at: new Date().toISOString(),
        party_size: r.kind === "walkin" ? Number(r.dayPass?.party_size || 1) : 1,
      };
      const { error } = await sb.from("access_sessions").insert(payload);
      if (error) throw error;
      toast({ title: "Checked in" });
      setRefreshKey((k) => k + 1);
    } catch (e: any) {
      toast({ title: "Check-in failed", description: e?.message || "Try again.", variant: "destructive" });
    }
  };

  const checkOut = async (customerId: string) => {
    if (!business?.id) return;
    try {
      const { data: session, error: sErr } = await sb
        .from("access_sessions")
        .select("id")
        .eq("business_id", business.id)
        .eq("customer_id", customerId)
        .is("check_out_at", null)
        .order("check_in_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (sErr) throw sErr;
      if (!session?.id) {
        toast({ title: "Not checked in", description: "No open session found." });
        return;
      }
      const { error } = await sb.from("access_sessions").update({ check_out_at: new Date().toISOString() }).eq("id", session.id);
      if (error) throw error;
      toast({ title: "Checked out" });
      setRefreshKey((k) => k + 1);
    } catch (e: any) {
      toast({ title: "Check-out failed", description: e?.message || "Try again.", variant: "destructive" });
    }
  };

  const createWalkIn = async () => {
    if (!business?.id) return;
    if (!walkIn.name.trim()) return;
    
    // If Paystack payment, handle differently
    if (walkIn.method === "paystack") {
      if (!walkIn.email.trim()) {
        toast({ title: "Email required", description: "Email is required for Paystack payments.", variant: "destructive" });
        return;
      }
      
      setStartingPaystackPayment(true);
      try {
        // Find or create customer (handles duplicate email gracefully)
        const normalizedEmail = walkIn.email.trim().toLowerCase();
        const { data: customerId, error: customerError } = await sb.rpc(
          "find_or_create_customer_public",
          {
            p_business_id: business.id,
            p_name: walkIn.name.trim(),
            p_email: normalizedEmail,
            p_phone: walkIn.phone.trim() || null,
            p_notes: "Walk-in",
          } as any
        );
        if (customerError) throw customerError;
        if (!customerId) throw new Error("Failed to create customer");

        const amount = Number(walkIn.amount || 0);
        const partySize = Math.max(1, Number(walkIn.partySize || 1));
        
        // Create day pass with unpaid status (will be updated to paid after Paystack payment)
        const { data: pass, error: pErr } = await sb
          .from("day_passes")
          .insert({
            business_id: business.id,
            customer_id: customerId,
            pass_date: format(new Date(), "yyyy-MM-dd") as any,
            amount,
            currency: (business as any)?.currency || "GHS",
            payment_method: "paystack",
            payment_status: "unpaid",
            party_size: partySize,
          } as any)
          .select()
          .single();
        if (pErr) throw pErr;

        // Initialize Paystack payment
        const callbackUrl = buildPaystackCallbackUrl("/app/access-desk");
        const { data, error } = await sb.functions.invoke("paystack-init", {
          body: {
            context: { type: "day_pass_payment", dayPassId: pass.id },
            payerEmail: walkIn.email.trim(),
            payerName: walkIn.name.trim() || null,
            callbackUrl,
          },
        });
        if (error) throw error;
        const url = String((data as any)?.authorizationUrl || "");
        if (!url) throw new Error("Missing Paystack authorizationUrl");
        
        // Close dialog and redirect to Paystack
        setWalkInOpen(false);
        
        // Safari mobile fix: Use assign() with a small delay to prevent blocking
        setTimeout(() => {
          window.location.assign(url);
        }, 50);
      } catch (e: any) {
        toast({ title: "Failed to start payment", description: e?.message || "Try again.", variant: "destructive" });
      } finally {
        setStartingPaystackPayment(false);
      }
      return;
    }

    // For non-Paystack methods (cash, momo, etc.), create immediately
    setIsCreatingWalkIn(true);
    try {
      // Find or create customer (handles duplicate email gracefully)
      const normalizedEmail = walkIn.email.trim() ? walkIn.email.trim().toLowerCase() : null;
      const { data: customerId, error: customerError } = await sb.rpc(
        "find_or_create_customer_public",
        {
          p_business_id: business.id,
          p_name: walkIn.name.trim(),
          p_email: normalizedEmail || null,
          p_phone: walkIn.phone.trim() || null,
          p_notes: "Walk-in",
        } as any
      );
      if (customerError) throw customerError;
      if (!customerId) throw new Error("Failed to create customer");

      const amount = Number(walkIn.amount || 0);
      const partySize = Math.max(1, Number(walkIn.partySize || 1));
      const { data: pass, error: pErr } = await sb
        .from("day_passes")
        .insert({
          business_id: business.id,
          customer_id: customerId,
          pass_date: format(new Date(), "yyyy-MM-dd") as any,
          amount,
          currency: (business as any)?.currency || "GHS",
          payment_method: walkIn.method,
          payment_status: "paid",
          party_size: partySize,
        } as any)
        .select()
        .single();
      if (pErr) throw pErr;

      // Record payment (optional but useful)
      await sb.from("access_payments").insert({
        business_id: business.id,
        customer_id: customerId,
        day_pass_id: pass.id,
        amount,
        currency: (business as any)?.currency || "GHS",
        status: "paid",
        method: walkIn.method,
      } as any);

      // Auto check-in
      await sb.from("access_sessions").insert({
        business_id: business.id,
        customer_id: customerId,
        day_pass_id: pass.id,
        check_in_at: new Date().toISOString(),
        party_size: partySize,
      } as any);

      toast({ title: "Walk-in created", description: `Ticket ${pass.ticket_code}` });
      setWalkInOpen(false);
      setWalkIn({ name: "", phone: "", email: "", amount: "", method: "cash", partySize: "1" });
      setRefreshKey((k) => k + 1);

      // Open printable ticket in a new tab
      window.open(`${getBaseUrl()}/app/access-desk/ticket/${pass.id}`, "_blank");
    } catch (e: any) {
      toast({ title: "Failed to create walk-in", description: e?.message || "Try again.", variant: "destructive" });
    } finally {
      setIsCreatingWalkIn(false);
    }
  };

  return (
    <AppShell businessName={business?.name || "My Business"}>
      <PageHeader title="Access Desk" description="Check members and walk-ins in/out" />

      <div className="mb-4 grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="rounded-xl border border-border bg-card p-4">
          <p className="text-xs text-muted-foreground">Visits today</p>
          <p className="mt-2 text-2xl font-semibold text-foreground">
            {summaryLoading ? "…" : summary.visitsToday}
          </p>
        </div>
        <div className="rounded-xl border border-border bg-card p-4">
          <p className="text-xs text-muted-foreground">Member visits</p>
          <p className="mt-2 text-2xl font-semibold text-foreground">
            {summaryLoading ? "…" : summary.memberVisitsToday}
          </p>
        </div>
        <div className="rounded-xl border border-border bg-card p-4">
          <p className="text-xs text-muted-foreground">Walk-ins</p>
          <p className="mt-2 text-2xl font-semibold text-foreground">
            {summaryLoading ? "…" : summary.walkInVisitsToday}
          </p>
        </div>
        <div className="rounded-xl border border-border bg-card p-4">
          <p className="text-xs text-muted-foreground">Walk-in revenue (today)</p>
          <p className="mt-2 text-2xl font-semibold text-foreground">
            {summaryLoading ? "…" : `${(business as any)?.currency || "GHS"} ${summary.walkInRevenueToday.toFixed(2)}`}
          </p>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-xl border border-border bg-card p-4">
          <div className="flex items-center justify-between gap-3 mb-3">
            <p className="font-semibold text-foreground">Search</p>
            <Button size="pill" onClick={() => setWalkInOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              New walk-in
            </Button>
          </div>
          <div className="space-y-2">
            <Label>Membership ID / Ticket / Name / Phone</Label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="e.g. TSC-000123 or TSC-WI-260109-1530-0001 or Ama"
                  className="pl-9"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") search();
                  }}
                />
              </div>
              <Button onClick={search} disabled={loading || !query.trim()}>
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Find"}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">Today: {todayLabel} • Tip: search by membership code for fastest check-in.</p>
          </div>

          <div className="mt-4 space-y-2">
            {results.length === 0 ? (
              <p className="text-sm text-muted-foreground">No results yet.</p>
            ) : (
              results.map((r, idx) => (
                <div key={idx} className="rounded-lg border border-border bg-background p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-medium text-foreground truncate">{r.customer?.name || "Customer"}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {r.kind === "member" ? (
                          <>
                            Member • <span className="font-mono">{r.membership.membership_code}</span> • {r.membership.status} • Expires{" "}
                            {r.membership.expires_at}
                          </>
                        ) : (
                          <>
                            Walk-in • <span className="font-mono">{r.dayPass.ticket_code}</span> • {r.dayPass.currency}{" "}
                            {Number(r.dayPass.amount || 0).toFixed(2)}
                          </>
                        )}
                      </p>
                    </div>
                    <div className="flex flex-col gap-2">
                      <Button size="sm" onClick={() => checkIn(r)}>
                        <LogIn className="mr-2 h-4 w-4" />
                        Check in
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => checkOut(r.customer.id)}>
                        <LogOut className="mr-2 h-4 w-4" />
                        Check out
                      </Button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="rounded-xl border border-border bg-card p-4">
          <p className="font-semibold text-foreground mb-3">Currently inside</p>
          {openSessions.length === 0 ? (
            <p className="text-sm text-muted-foreground">No one is currently checked in.</p>
          ) : (
            <div className="space-y-2">
              {openSessions.map((s: any) => (
                <div key={s.id} className="rounded-lg border border-border bg-background p-3 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-medium text-foreground truncate">{s.customer?.name || "Customer"}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Checked in: {format(new Date(s.check_in_at), "h:mm a")}
                      {Number(s.party_size || 1) > 1 ? ` • Party: ${Number(s.party_size || 1)}` : ""}
                    </p>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => checkOut(s.customer.id)}>
                    <LogOut className="mr-2 h-4 w-4" />
                    Check out
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="mt-6 rounded-xl border border-border bg-card p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="font-semibold text-foreground">Visits log</p>
            <p className="text-xs text-muted-foreground">Filter and export check-ins/outs</p>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <Select value={rangePreset} onValueChange={(v) => setRangePreset(v as RangePreset)}>
              <SelectTrigger className="w-full sm:w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="today">Today</SelectItem>
                <SelectItem value="week">This week</SelectItem>
                <SelectItem value="month">This month</SelectItem>
                <SelectItem value="custom">Custom</SelectItem>
              </SelectContent>
            </Select>

            {rangePreset === "custom" ? (
              <div className="flex gap-2">
                <Input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} />
                <Input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} />
              </div>
            ) : null}

            <Button variant="outline" onClick={exportVisitsCSV} disabled={visitsLoading || visitsRows.length === 0}>
              <Download className="mr-2 h-4 w-4" />
              Export
            </Button>
          </div>
        </div>

        <div className="mt-4">
          {visitsLoading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-7 w-7 animate-spin text-muted-foreground" />
            </div>
          ) : visitsRows.length === 0 ? (
            <p className="text-sm text-muted-foreground">No visits found for this date range.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Check in</TableHead>
                  <TableHead>Check out</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visitsRows.map((r: any) => {
                  const isMember = !!r.membership;
                  const name = r.customer?.name || "—";
                  const membershipType = isMember ? (r.membership?.plan?.name || "Membership") : "Day Pass";
                  const partySize = Number(r.party_size || 1);
                  const date = r.check_in_at ? format(new Date(r.check_in_at), "MMM d, yyyy") : "—";
                  const checkIn = r.check_in_at ? format(new Date(r.check_in_at), "h:mm a") : "—";
                  const checkOut = r.check_out_at ? format(new Date(r.check_out_at), "h:mm a") : "—";

                  return (
                    <TableRow key={r.id}>
                      <TableCell className="font-medium">
                        <div className="min-w-0">
                          <div className="truncate">{name}</div>
                          {!isMember && r.day_pass?.ticket_code ? (
                            <div className="text-xs text-muted-foreground font-mono truncate">{r.day_pass.ticket_code}</div>
                          ) : isMember && r.membership?.membership_code ? (
                            <div className="text-xs text-muted-foreground font-mono truncate">{r.membership.membership_code}</div>
                          ) : null}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap items-center gap-2">
                          <Badge variant="secondary">{isMember ? "Member" : "Walk-in"}</Badge>
                          <span className="text-xs text-muted-foreground">{membershipType}</span>
                          {partySize > 1 ? <Badge variant="outline">Party {partySize}</Badge> : null}
                        </div>
                      </TableCell>
                      <TableCell>{date}</TableCell>
                      <TableCell>{checkIn}</TableCell>
                      <TableCell>{checkOut}</TableCell>
                      <TableCell className="text-right">
                        {!isMember && r.day_pass?.id ? (
                          <Button
                            variant="outline"
                            size="sm"
                            asChild
                            className="text-xs"
                          >
                            <Link to={`/app/access-desk/ticket/${r.day_pass.id}`} target="_blank">
                              <Printer className="mr-1 h-3 w-3" />
                              Print Ticket
                            </Link>
                          </Button>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </div>
      </div>

      <Dialog open={walkInOpen} onOpenChange={setWalkInOpen}>
        <DialogContent className="sm:max-w-md w-[calc(100%-2rem)] max-h-[90vh] p-0 gap-0 overflow-hidden rounded-2xl flex flex-col">
          <DialogHeader className="px-6 pt-6 pb-4 flex-shrink-0">
            <DialogTitle>New walk-in day pass</DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto px-6 pb-4 flex-1 min-h-0">
            <div className="space-y-4">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input value={walkIn.name} onChange={(e) => setWalkIn((w) => ({ ...w, name: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label>Phone (optional)</Label>
              <Input value={walkIn.phone} onChange={(e) => setWalkIn((w) => ({ ...w, phone: e.target.value }))} />
            </div>
            {walkIn.method === "paystack" && (
              <div className="space-y-2">
                <Label>Email <span className="text-destructive">*</span></Label>
                <Input 
                  type="email" 
                  value={walkIn.email} 
                  onChange={(e) => setWalkIn((w) => ({ ...w, email: e.target.value }))} 
                  placeholder="required for Paystack"
                />
              </div>
            )}
            <div className="space-y-2">
              <Label>Party size</Label>
              <Input
                type="number"
                min={1}
                value={walkIn.partySize}
                onChange={(e) => setWalkIn((w) => ({ ...w, partySize: e.target.value }))}
              />
              <p className="text-xs text-muted-foreground">For groups, you can collect just one name/phone and set the party size.</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Amount</Label>
                <Input type="number" value={walkIn.amount} onChange={(e) => setWalkIn((w) => ({ ...w, amount: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label>Payment</Label>
                <Select value={walkIn.method} onValueChange={(v) => setWalkIn((w) => ({ ...w, method: v }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="paystack">Paystack (Card, Mobile Money, Bank)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button 
              className="w-full" 
              onClick={createWalkIn} 
              disabled={isCreatingWalkIn || startingPaystackPayment || (walkIn.method === "paystack" && !walkIn.email.trim())}
            >
              {isCreatingWalkIn || startingPaystackPayment ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {startingPaystackPayment ? "Redirecting to Paystack…" : "Creating..."}
                </>
              ) : walkIn.method === "paystack" ? (
                "Pay with Paystack"
              ) : (
                <>
                  <Printer className="mr-2 h-4 w-4" />
                  Create & Print Ticket
                </>
              )}
            </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}


